import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'

import moment from 'moment'

import { NIF } from '../../Components/NIF'
import { SelectedEntity } from '../../Components/SelectedEntity'
import { usePolicyholderDetailsStore } from '../../Stores/policyholderDetailsStore'
import { useReportStore } from '../../Stores/reportStore'
import { useSimulatorStore } from '../../Stores/simulatorStore'
import { useGenerateProposalDocumentsStore } from '../../Stores/useGenerateProposalDocumentsStore'
import { useRunPetisSimulationStore } from '../../Stores/useRunPetisSimulationStore'
import { SESSION_STORAGE } from '../../constants/sessionStorage'
import { useGetEntitiesDetails } from '../../hooks/api/useGetEntitiesDetails'
import { getErrorMessage } from '../../hooks/useErrorRequest'
import { t } from '../../i18n'
import { validateNIF } from '../../utils/validates'
import { EntityContainer } from './style'

type EntityProps = {
  initialDate: string
  disabled?: boolean
  setLoading?: (value: boolean) => void
  setHasEnableGetEntities?: (value: boolean) => void
  setIsFiscalNumberFilled?: (value: boolean) => void
  setIsFiscalNumberSearched?: (value: boolean) => void
}

const NOT_FOUND = 404
const elementPortal = document.getElementsByTagName('body')?.[0]

export const Entity = ({
  initialDate,
  setLoading,
  disabled,
  setIsFiscalNumberFilled = () => {},
  setIsFiscalNumberSearched = () => {},
}: EntityProps) => {
  const [entityMessage, setEntityMessage] = useState<string | null>(null)
  const { setEnabledRunPetisRequest } = useRunPetisSimulationStore()
  const { isGenerateProposalDocuments } = useGenerateProposalDocumentsStore()
  const { resetPolicyholderDetails } = usePolicyholderDetailsStore()
  const { isProspect } = useReportStore()
  const {
    entitySelected,
    setEntity,
    setEntitySelected,
    ownerFiscalNumber,
    endDate,
    isNPALeadsContext,
    setIsNPALeadsContext,
    isNPANavigationClientContext,
    setIsNPANavigationClientContext,
    showSelectedEntity,
    setOwnerFiscalNumber,
    setShowSelectedEntity,
    isClientContext,
    setIsClientContext,
    setEntitySelectedAddress,
    resetEntityData,
  } = useSimulatorStore()
  const [vat, setVat] = useState<string>(ownerFiscalNumber)
  const {
    mutate,
    data: dataEntityDetail,
    isLoading,
    isSuccess,
    error,
    reset: resetEntityDetail,
  } = useGetEntitiesDetails()

  const getEntity = (_vatNumber: string) => {
    setEntityMessage(null)
    setVat(_vatNumber)
    mutate({ vatNumber: _vatNumber })
  }

  useEffect(() => {
    if (dataEntityDetail) {
      const entity = dataEntityDetail?.matchedEntities?.[0]

      setEntity({
        address: {
          postalCode: entity?.addresses?.[0]?.postalCode || null,
          addressPointer: null,
        },
        contactDetails: {
          phonePointer: entity?.entityPhone || null,
          mailingAddressPointer: null,
        },
        entityId: entity?.idEntity || null,
        name: entity?.name || null,
        birthDate: entity?.birthdate || null,
        initialDate:
          moment(initialDate, 'DD-MM-YYYY').format('YYYY-MM-DD') || null,
        endDate,
        idExternal: entity?.idEntity || null,
        role: null,
        payer: null,
        vatNumber: vat,
      })
    }
  }, [dataEntityDetail, initialDate, setEntity, vat])

  useEffect(() => {
    const NPANavigationClientContext = JSON.parse(
      sessionStorage.getItem(SESSION_STORAGE.NPANavigationClientContext) ||
        '{}',
      )

    if (
      isClientContext &&
      (NPANavigationClientContext.data.clientEntityID || '').length > 0
    ) {
      setOwnerFiscalNumber(NPANavigationClientContext.data.nif || '')
      setEntitySelected(
        dataEntityDetail?.matchedEntities?.find(
          (entity) =>
            entity.idEntity === NPANavigationClientContext.data.clientEntityID,
        ) as never,
      )
    } else {
      if ((dataEntityDetail?.matchedEntities || [])?.length === 1 && dataEntityDetail?.referrals?.[0]?.outcomeTypeCode !== "S") {
        setOwnerFiscalNumber(
          dataEntityDetail?.matchedEntities?.[0]?.vatNumber || '',
        )
        setEntitySelected(dataEntityDetail?.matchedEntities?.[0] as never)
        setEntitySelectedAddress(
          dataEntityDetail?.matchedEntities?.[0]?.addresses?.[0] as never,
        )
      }
      else {
        setEntityMessage(dataEntityDetail?.referrals?.[0]?.outcomeMessage ?? "")
        resetEntityData()
        resetPolicyholderDetails()
        setEntitySelected(null)
        setOwnerFiscalNumber("")
      }

      if ((dataEntityDetail?.matchedEntities || [])?.length > 1) {
        setShowSelectedEntity(true)
      }
    }
  }, [dataEntityDetail, setEntitySelected, isClientContext])

  useEffect(() => {
    setLoading && setLoading(isLoading)
  }, [isLoading, setLoading])

  useEffect(() => {
    if (error?.response?.status === NOT_FOUND) {
      const messageError = getErrorMessage(error.response.data)
      setEntityMessage(messageError)
      resetEntityData()
    }
  }, [error])

  useEffect(() => {
    if (
      !isClientContext &&
      validateNIF(vat || '') &&
      isSuccess &&
      (dataEntityDetail?.matchedEntities || []).length > 1 &&
      (entitySelected?.vatNumber || '').length === 0
    ) {
      setShowSelectedEntity(true)
      setEntityMessage(null)
    }
  }, [
    isSuccess,
    dataEntityDetail?.matchedEntities,
    entitySelected?.vatNumber,
    vat,
    isClientContext,
  ])

 useEffect(() => {
    const NPANavigationClientContext = JSON.parse(
      sessionStorage.getItem(SESSION_STORAGE.NPANavigationClientContext) ||
        '{}',
      )

    if (
      Object.values(NPANavigationClientContext).length &&
      isNPANavigationClientContext &&
      validateNIF(NPANavigationClientContext.refNumber)
    ) {
      getEntity(NPANavigationClientContext.refNumber)
      setIsNPANavigationClientContext(false)
      setIsClientContext(true)
    }
  }, [isNPANavigationClientContext])

  useEffect(() => {
    // New leadsContext -  NPA_LeadsContext
    const leadsContext = JSON.parse(sessionStorage.getItem(SESSION_STORAGE.NPALeadsContext) ||
        '{}',
    )
    const leadsNifParsed = leadsContext?.context?.client_nif || '';
    if (Object.values(leadsContext).length && leadsContext && leadsNifParsed) {
        getEntity(leadsNifParsed);
        setIsNPALeadsContext(false)
    }
  }, [isNPALeadsContext])

  useEffect(() => {
    if (isProspect) {
      getEntity(ownerFiscalNumber)
    }
  }, [isProspect])

  return (
    <>
      <EntityContainer>
        <div className="nif">
          <NIF
            optionalLabel={true}
            errorMessage={entityMessage}
            onClick={() => {
              resetEntityDetail()
              resetPolicyholderDetails()
              setIsFiscalNumberSearched(true)
              if (validateNIF(vat)) {
                setEntityMessage(null)
                getEntity(vat!)
                setEnabledRunPetisRequest(true)
              } else {
                setEntityMessage(t('general.errors.fiscalNumberInvalid'))
                setEntitySelected(null)
              }
            }}
            readOnly={
              ((entitySelected?.vatNumber || '')?.length > 0 &&
                isClientContext) ||
              isGenerateProposalDocuments ||
              disabled
            }
            onValueChanged={(value: any) => {
              if (value.length > 0) {
                if (validateNIF(value)) {
                  setEntityMessage(t('general.search'))
                  setEntitySelected(null)
                  setIsFiscalNumberFilled(true)
                } else {
                  setEntityMessage(t('general.errors.fiscalNumberInvalid'))
                }
              } else {
                setEntityMessage('')
                setIsFiscalNumberFilled(false)
                setEntitySelected(null)
              }
              setVat(value)
            }}
            required={false}
            value={ownerFiscalNumber || entitySelected?.vatNumber! || vat}
          />
        </div>
      </EntityContainer>
      {createPortal(
        <>
          <SelectedEntity
            isVisible={showSelectedEntity}
            data={dataEntityDetail?.matchedEntities as never}
            closeAction={() => setShowSelectedEntity(false)}
          />
        </>,
        elementPortal!,
      )}
    </>
  )
}
